import React, { useEffect, useRef, useState } from 'react'
import {
    ButtonDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Input,
    Button,
} from 'reactstrap'

export default function SavedViewsButton({
    views,
    activeViewName,
    onApply,
    onSaveAs,
    onDelete,
    onReset,
    saving,
}) {
    const [open, setOpen] = useState(false)
    const [saveOpen, setSaveOpen] = useState(false)
    const [draftName, setDraftName] = useState('')
    const [deleteTarget, setDeleteTarget] = useState(null) // string | null
    const nameInputRef = useRef(null)

    const entries = Object.entries(views || {})

    useEffect(() => {
        if (saveOpen) {
            setDraftName(activeViewName || '')
            // Focus on next tick (after modal mounts).
            setTimeout(() => nameInputRef.current?.focus(), 0)
        }
    }, [saveOpen, activeViewName])

    const trimmedName = draftName.trim()
    const canSave = trimmedName.length > 0 && trimmedName.length <= 60

    const submitSave = () => {
        if (!canSave) return
        onSaveAs(trimmedName.slice(0, 60))
        setSaveOpen(false)
        setDraftName('')
    }

    const confirmDelete = () => {
        if (deleteTarget) onDelete(deleteTarget)
        setDeleteTarget(null)
    }

    return (
        <>
            <ButtonDropdown
                isOpen={open}
                toggle={() => setOpen((x) => !x)}
                className="cd-savedviews"
            >
                <button
                    type="button"
                    className="cd-savedviews-main"
                    onClick={() => {
                        if (activeViewName) onApply(activeViewName)
                        else setOpen(true)
                    }}
                    title={activeViewName ? `Re-apply "${activeViewName}"` : 'No view selected'}
                >
                    <i className="fa fa-bookmark-o" />
                    <span className="label">
                        {activeViewName || 'No view'}
                    </span>
                </button>
                <DropdownToggle
                    className="cd-savedviews-caret"
                    aria-label="Open saved views menu"
                >
                    <i className={`fa fa-chevron-${open ? 'up' : 'down'}`} />
                </DropdownToggle>
                <DropdownMenu right className="cd-savedviews-menu">
                    {entries.length === 0 && (
                        <DropdownItem disabled className="cd-savedviews-empty">
                            No saved views yet
                        </DropdownItem>
                    )}
                    {entries.map(([name]) => {
                        const isActive = name === activeViewName
                        return (
                            <div
                                key={name}
                                className={`cd-savedviews-item${isActive ? ' active' : ''}`}
                            >
                                <button
                                    type="button"
                                    className="apply"
                                    onClick={() => {
                                        onApply(name)
                                        setOpen(false)
                                    }}
                                >
                                    {isActive && <i className="fa fa-check" />}
                                    <span>{name}</span>
                                </button>
                                <button
                                    type="button"
                                    className="delete"
                                    title="Delete view"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setDeleteTarget(name)
                                    }}
                                >
                                    <i className="fa fa-times" />
                                </button>
                            </div>
                        )
                    })}
                    <DropdownItem divider />
                    <DropdownItem
                        onClick={(e) => {
                            e.preventDefault()
                            setSaveOpen(true)
                        }}
                        disabled={saving}
                    >
                        <i className="fa fa-plus" />{' '}
                        {saving ? 'Saving…' : 'Save current as view…'}
                    </DropdownItem>
                    <DropdownItem
                        onClick={(e) => {
                            e.preventDefault()
                            onReset()
                        }}
                    >
                        <i className="fa fa-refresh" /> Reset to defaults
                    </DropdownItem>
                </DropdownMenu>
            </ButtonDropdown>

            <Modal
                isOpen={saveOpen}
                toggle={() => setSaveOpen(false)}
                className="cd-savedviews-modal"
                centered
            >
                <ModalHeader toggle={() => setSaveOpen(false)}>
                    Save current filters as view
                </ModalHeader>
                <ModalBody>
                    <label
                        htmlFor="cd-savedviews-name"
                        style={{
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            color: '#6b7280',
                            marginBottom: 6,
                        }}
                    >
                        View name
                    </label>
                    <Input
                        innerRef={nameInputRef}
                        id="cd-savedviews-name"
                        type="text"
                        value={draftName}
                        maxLength={60}
                        placeholder="e.g. Wednesday actives"
                        onChange={(e) => setDraftName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') submitSave()
                        }}
                    />
                    {views?.[trimmedName] && (
                        <div style={{ marginTop: 8, fontSize: 12, color: '#b45309' }}>
                            A view with this name already exists — it will be overwritten.
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" outline onClick={() => setSaveOpen(false)}>
                        Cancel
                    </Button>
                    <Button color="primary" disabled={!canSave || saving} onClick={submitSave}>
                        {saving ? 'Saving…' : 'Save view'}
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal
                isOpen={!!deleteTarget}
                toggle={() => setDeleteTarget(null)}
                className="cd-savedviews-modal"
                centered
            >
                <ModalHeader toggle={() => setDeleteTarget(null)}>
                    Delete saved view
                </ModalHeader>
                <ModalBody>
                    Delete the saved view{' '}
                    <strong>{deleteTarget}</strong>? This can't be undone.
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" outline onClick={() => setDeleteTarget(null)}>
                        Cancel
                    </Button>
                    <Button color="danger" onClick={confirmDelete} disabled={saving}>
                        Delete
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    )
}
